import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { Field, Note, Problem, button, input } from '../components/form.js';
import { Select } from '../components/Select.js';
import { AttachmentPicker, MessageFiles, useAttachments } from '../components/Attachments.js';
import {
  BODY_MIN,
  FEEDBACK_KINDS,
  FEEDBACK_MAX,
  SOMETHING_ELSE,
  authorOf,
  feedbackProblem,
  newRequestProblem,
  requestSubject,
  statusWords,
  supportApi,
  ticketPath,
  topicChoices,
  type FeedbackKind,
  type SupportApi,
  type SupportProduct,
  type Ticket,
  type TicketRow,
} from '../lib/support.js';
import { formatDay } from '../lib/subscriptions.js';
import { useEmailVerified } from '../lib/email-verification.js';

/**
 * "Contact support" (Jon, 2026-09-27): a person's own tickets, a form to open
 * one, and each ticket's conversation with a reply box.
 *
 * `/support` is the list and the form, `/support/<reference>` one ticket. The
 * data is the support service's, reached with a token for its audience
 * (lib/support-token.ts), so the first visit on a page makes one silent round
 * trip through the sign-in service and comes back here.
 *
 * `api` is a parameter so the tests can hand in a fake; the app passes nothing.
 */
const defaultApi = supportApi();

/**
 * `/support?topic=...`: another screen sending somebody here with a topic in
 * mind, such as the date-of-birth refusal on Profile (auth#672). The form
 * opens with that topic chosen once a product is.
 */
export function topicFromLocation(search: string = window.location.search): string | null {
  const topic = new URLSearchParams(search).get('topic')?.trim();
  return topic ? topic.slice(0, 200) : null;
}

export function Support({
  api = defaultApi,
  initialTopic = topicFromLocation(),
}: {
  api?: SupportApi;
  initialTopic?: string | null;
}) {
  const [tickets, setTickets] = useState<TicketRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<'request' | 'feedback' | null>(initialTopic ? 'request' : null);
  /** 1 business day once the email is verified, 3 until then (Jon, 2026-10-04, platform#46). */
  const verified = useEmailVerified();

  async function load() {
    try {
      const res = await api.tickets();
      setTickets(res.tickets ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Your requests could not be loaded.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (error && !tickets) return <Problem>{error}</Problem>;
  if (!tickets) return <Note>Loading…</Note>;

  return (
    <section style={{ maxWidth: 640 }}>
      <div style={header}>
        <div style={{ minWidth: 0 }}>
          <h2 style={title}>Support</h2>
          <Note>
            Ask us about any Ridgewill product. We reply by email and here, within{' '}
            {verified ? '1 business day' : '3 business days, or 1 once you verify your email'}.
          </Note>
        </div>
        {!form && (
          <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setForm('request')} style={{ ...button, marginTop: 0 }}>
              Contact support
            </button>
            <button type="button" onClick={() => setForm('feedback')} style={{ ...button, marginTop: 0 }}>
              Send feedback
            </button>
          </div>
        )}
      </div>

      {form === 'request' && (
        <NewRequest
          api={api}
          initialTopic={initialTopic}
          onCancel={() => setForm(null)}
          onOpened={(reference) => window.location.assign(ticketPath(reference))}
        />
      )}
      {form === 'feedback' && <SendFeedback api={api} onDone={() => setForm(null)} />}

      {tickets.length === 0 ? (
        !form && <Note>You have not contacted support yet.</Note>
      ) : (
        <ul style={list} aria-label="Your requests">
          {tickets.map((t) => (
            <li key={t.reference} style={row}>
              <a href={ticketPath(t.reference)} style={rowLink}>
                <span style={{ display: 'flex', gap: '.6rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong style={{ color: C.text }}>{t.subject}</strong>
                  <StatusPill status={t.status} />
                </span>
                <span style={meta}>
                  {t.reference} · updated {formatDay(t.updatedAt)}
                </span>
                {t.lastMessage && <span style={preview}>{t.lastMessage.preview}</span>}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function NewRequest({
  api,
  initialTopic = null,
  onCancel,
  onOpened,
}: {
  api: SupportApi;
  initialTopic?: string | null;
  onCancel: () => void;
  onOpened: (reference: string) => void;
}) {
  const [products, setProducts] = useState<SupportProduct[] | null>(null);
  const [product, setProduct] = useState('');
  /** Null while the product's topics load. */
  const [topics, setTopics] = useState<string[] | null>(null);
  const [topic, setTopic] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attachments = useAttachments(api);

  useEffect(() => {
    api
      .products()
      .then((res) => {
        const list = res.products ?? [];
        setProducts(list);
        // One choice is no choice: take it rather than asking.
        if (list.length === 1) setProduct(list[0]!.slug);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Support could not be reached.'));
  }, [api]);

  /**
   * The topic list is per product (auth#672): support's form mapping decides
   * which topics each product lists, in the order staff set. Loaded again on
   * every product change. When it cannot be loaded the form still works, with
   * "Something else" and a subject, which routes like any untopiced ticket.
   */
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    setTopics(null);
    const settle = (list: string[]) => {
      if (cancelled) return;
      const choices = topicChoices(list);
      setTopics(choices);
      setTopic((current) => {
        if (current && choices.includes(current)) return current;
        if (initialTopic && choices.includes(initialTopic)) return initialTopic;
        // One choice is no choice: take it rather than asking.
        return choices.length === 1 ? choices[0]! : '';
      });
      // Sent here for a topic this product does not list (or the list did not
      // load): keep what they came for, as the subject of "Something else".
      if (initialTopic && !choices.includes(initialTopic)) setSubject((s) => s || initialTopic);
    };
    api
      .topics(product)
      .then((res) => settle(res.topics ?? []))
      .catch(() => settle([]));
    return () => {
      cancelled = true;
    };
  }, [api, product, initialTopic]);

  const missing = newRequestProblem({ product, topic, subject, body });
  const blocked = busy || missing !== null || attachments.uploading;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.open({
        product,
        ...requestSubject({ topic, subject }),
        body: body.trim(),
        ...(attachments.keys.length ? { attachmentKeys: attachments.keys } : {}),
      });
      onOpened(res.reference);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Your request could not be sent.');
      setBusy(false);
    }
  }

  return (
    <form
      style={card}
      aria-label="Contact support"
      onSubmit={(e) => {
        e.preventDefault();
        if (!blocked) void send();
      }}
    >
      <Field label="What is it about?">
        {products ? (
          <Select
            block
            label="What is it about?"
            value={product}
            onChange={setProduct}
            options={[
              ...(product ? [] : [{ value: '', label: 'Choose…' }]),
              ...products.map((p) => ({ value: p.slug, label: p.name })),
            ]}
          />
        ) : (
          <Note>Loading…</Note>
        )}
      </Field>
      {product && (
        <Field label="Topic">
          {topics ? (
            <Select
              block
              label="Topic"
              value={topic}
              onChange={setTopic}
              placeholder="Choose a topic"
              options={topics.map((t) => ({ value: t, label: t }))}
            />
          ) : (
            <Note>Loading…</Note>
          )}
        </Field>
      )}
      {/* Only for "Something else": every other topic is the subject. */}
      {topic === SOMETHING_ELSE && (
        <Field id="support-subject" label="Subject">
          <input
            id="support-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            style={input}
          />
        </Field>
      )}
      <Field id="support-body" label="How can we help?">
        <textarea
          id="support-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          maxLength={20000}
          style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </Field>
      <AttachmentPicker attachments={attachments} disabled={busy} />
      {error && <Problem>{error}</Problem>}
      <div style={actions}>
        <button type="submit" disabled={blocked} style={primary(blocked)}>
          {busy ? 'Sending…' : attachments.uploading ? 'Uploading…' : 'Send'}
        </button>
        <button type="button" onClick={onCancel} style={{ ...button, marginTop: 0 }}>
          Cancel
        </button>
        {missing && (subject || body) ? <span style={hint}>Needs: {missing}</span> : null}
      </div>
    </form>
  );
}

/**
 * Feedback (account#98): kept and read, never answered as a thread. Support has
 * no list of it for the sender, so the sent state is the only receipt, and it
 * says plainly that someone who needs an answer should open a request instead.
 *
 * "May we contact you" starts unticked, as support's own default does: the
 * address is only stored when it is ticked, so leaving it alone is the private
 * choice.
 */
function SendFeedback({ api, onDone }: { api: SupportApi; onDone: () => void }) {
  const [products, setProducts] = useState<SupportProduct[] | null>(null);
  const [product, setProduct] = useState('');
  const [kind, setKind] = useState<FeedbackKind>('IDEA');
  const [message, setMessage] = useState('');
  const [mayContact, setMayContact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ product: string; mayContact: boolean } | null>(null);

  useEffect(() => {
    api
      .products()
      .then((res) => {
        const list = res.products ?? [];
        setProducts(list);
        if (list.length === 1) setProduct(list[0]!.slug);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Support could not be reached.'));
  }, [api]);

  const missing = feedbackProblem({ product, message });
  const blocked = busy || missing !== null;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await api.feedback({ product, kind, message: message.trim(), mayContact });
      setSent({ product: products?.find((p) => p.slug === product)?.name ?? product, mayContact });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Your feedback could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div style={card} role="status">
        <h3 style={{ ...title, fontSize: '1rem' }}>Thanks, we have your feedback</h3>
        <Note>
          It has gone to the {sent.product} team.{' '}
          {sent.mayContact
            ? 'You said we may contact you, so we may email you if we have a question about it.'
            : 'You asked us not to contact you about it, so we will not.'}
        </Note>
        <Note>Feedback does not get a reply as a rule. If you need one, contact support instead.</Note>
        <div style={{ ...actions, marginTop: '1rem' }}>
          <button type="button" onClick={onDone} style={{ ...button, marginTop: 0 }}>
            Done
          </button>
          <button
            type="button"
            onClick={() => {
              setSent(null);
              setMessage('');
              setMayContact(false);
            }}
            style={{ ...button, marginTop: 0 }}
          >
            Send more feedback
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      style={card}
      aria-label="Send feedback"
      onSubmit={(e) => {
        e.preventDefault();
        if (!blocked) void send();
      }}
    >
      <p style={{ ...hint, margin: '0 0 1rem' }}>
        Tell us what is working and what is not. Feedback is read by the people who build the app. If something is
        broken and you need an answer, contact support instead.
      </p>
      <Field label="Which product?">
        {products ? (
          <Select
            block
            label="Which product?"
            value={product}
            onChange={setProduct}
            options={[
              ...(product ? [] : [{ value: '', label: 'Choose…' }]),
              ...products.map((p) => ({ value: p.slug, label: p.name })),
            ]}
          />
        ) : (
          <Note>Loading…</Note>
        )}
      </Field>
      <Field label="This is">
        <div role="radiogroup" aria-label="This is" style={choices}>
          {FEEDBACK_KINDS.map((k) => (
            <label key={k.kind} style={choice}>
              <input
                type="radio"
                name="feedback-kind"
                value={k.kind}
                checked={kind === k.kind}
                onChange={() => setKind(k.kind)}
                style={{ accentColor: C.brand, margin: 0 }}
              />
              {k.label}
            </label>
          ))}
        </div>
      </Field>
      <Field id="feedback-message" label="Message">
        <textarea
          id="feedback-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          maxLength={FEEDBACK_MAX}
          style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <span style={{ ...hint, display: 'block', textAlign: 'right', marginTop: '.3rem' }}>
          {message.length.toLocaleString('en-US')} of {FEEDBACK_MAX.toLocaleString('en-US')}
        </span>
      </Field>
      <label style={{ ...choice, marginBottom: '1.25rem' }}>
        <input
          type="checkbox"
          checked={mayContact}
          onChange={(e) => setMayContact(e.target.checked)}
          style={{ accentColor: C.brand, margin: 0 }}
        />
        You may contact me about this
      </label>
      {error && <Problem>{error}</Problem>}
      <div style={actions}>
        <button type="submit" disabled={blocked} style={primary(blocked)}>
          {busy ? 'Sending…' : 'Send feedback'}
        </button>
        <button type="button" onClick={onDone} style={{ ...button, marginTop: 0 }}>
          Cancel
        </button>
        {missing && message ? <span style={hint}>Needs: {missing}</span> : null}
      </div>
    </form>
  );
}

export function SupportTicket({ reference, api = defaultApi }: { reference: string; api?: SupportApi }) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState<'reply' | 'resolve' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const attachments = useAttachments(api);

  async function load() {
    try {
      setTicket(await api.ticket(reference));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'This request could not be loaded.');
    }
  }

  useEffect(() => {
    void load();
  }, [reference]);

  async function act(kind: 'reply' | 'resolve', run: () => Promise<unknown>) {
    setBusy(kind);
    setActionError(null);
    try {
      await run();
      if (kind === 'reply') {
        setReply('');
        attachments.reset();
      }
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusy(null);
    }
  }

  const back = (
    <a href="/support" style={backLink}>
      ← All requests
    </a>
  );

  if (error && !ticket) {
    return (
      <section style={{ maxWidth: 640 }}>
        {back}
        <Problem>{error}</Problem>
      </section>
    );
  }
  if (!ticket) return <Note>Loading…</Note>;

  const tooShort = reply.trim().length < BODY_MIN;
  const replyBlocked = busy !== null || tooShort || attachments.uploading;

  return (
    <section style={{ maxWidth: 640 }}>
      {back}
      <div style={{ ...header, marginTop: '.75rem' }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={title}>{ticket.subject}</h2>
          <span style={meta}>
            {ticket.reference} · opened {formatDay(ticket.openedAt)}
          </span>
        </div>
        <StatusPill status={ticket.status} />
      </div>

      <ol style={{ ...list, marginTop: '1.25rem' }} aria-label="Conversation">
        {ticket.messages.map((m) => (
          <li key={m.id} style={{ ...message, ...(m.direction === 'OUTBOUND' ? fromUs : {}) }}>
            <div style={messageHead}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.45rem', flexWrap: 'wrap' }}>
                <strong>{authorOf(m)}</strong>
                {m.aiWritten ? <AiAgentChip /> : null}
              </span>
              <span style={meta}>{formatDay(m.at)}</span>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{m.body}</div>
            <MessageFiles files={m.attachments ?? []} open={(id) => api.attachmentUrl(ticket.reference, id)} />
          </li>
        ))}
      </ol>

      <form
        style={{ marginTop: '1.25rem' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!replyBlocked) void act('reply', () => api.reply(ticket.reference, reply.trim(), ...(attachments.keys.length ? [attachments.keys] : [])));
        }}
      >
        <Field id="support-reply" label={ticket.status === 'RESOLVED' ? 'Reply to reopen it' : 'Reply'}>
          <textarea
            id="support-reply"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={4}
            maxLength={20000}
            style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>
        <AttachmentPicker attachments={attachments} disabled={busy !== null} />
        {actionError && <Problem>{actionError}</Problem>}
        <div style={actions}>
          <button type="submit" disabled={replyBlocked} style={primary(replyBlocked)}>
            {busy === 'reply' ? 'Sending…' : attachments.uploading ? 'Uploading…' : 'Send reply'}
          </button>
          {ticket.status !== 'RESOLVED' && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void act('resolve', () => api.resolve(ticket.reference))}
              style={{ ...button, marginTop: 0 }}
            >
              {busy === 'resolve' ? 'Marking…' : 'Mark as resolved'}
            </button>
          )}
          {reply && tooShort ? <span style={hint}>At least {BODY_MIN} characters.</span> : null}
        </div>
      </form>
    </section>
  );
}

/**
 * Marks a reply an AI agent wrote and sent on its own (support#81). Jon,
 * 2026-10-17: the chip is enough disclosure in the thread; the email carries
 * the full sentence. A reply a person sent, even from an agent's draft, has
 * none.
 */
function AiAgentChip() {
  return (
    <span
      title="This reply was written and sent by an AI Support Agent."
      style={{
        fontSize: '.72rem',
        fontWeight: 600,
        padding: '.1rem .5rem',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        background: C.violetTint,
        color: C.violetText,
      }}
    >
      AI agent
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  const waitingOnThem = status === 'WAITING_ON_USER';
  return (
    <span
      style={{
        fontSize: '.78rem',
        padding: '.15rem .55rem',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        border: `1px solid ${waitingOnThem ? C.brand : C.inputBorder}`,
        color: waitingOnThem ? C.brand : C.textMid,
      }}
    >
      {statusWords(status)}
    </span>
  );
}

const title: React.CSSProperties = { fontSize: '1.15rem', margin: '0 0 .35rem' };
const header: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '1rem',
  flexWrap: 'wrap',
};
const list: React.CSSProperties = { listStyle: 'none', padding: 0, margin: '1rem 0 0' };
const row: React.CSSProperties = { borderTop: `1px solid ${C.border}` };
const rowLink: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '.25rem',
  padding: '.85rem 0',
  textDecoration: 'none',
};
const meta: React.CSSProperties = { color: C.textMid, fontSize: '.85rem' };
const preview: React.CSSProperties = {
  color: C.textMid,
  fontSize: '.9rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const card: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: '1rem 1.1rem .25rem',
  margin: '1rem 0',
};
const actions: React.CSSProperties = { display: 'flex', gap: '.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' };
const hint: React.CSSProperties = { color: C.textMid, fontSize: '.85rem' };
const choices: React.CSSProperties = { display: 'flex', gap: '.5rem 1.25rem', flexWrap: 'wrap' };
const choice: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '.45rem', cursor: 'pointer' };
const primary = (disabled: boolean): React.CSSProperties => ({
  ...button,
  marginTop: 0,
  background: disabled ? 'transparent' : C.brand,
  color: disabled ? C.textMid : '#fff',
  borderColor: disabled ? C.inputBorder : C.brand,
  cursor: disabled ? 'default' : 'pointer',
});
const backLink: React.CSSProperties = { color: C.brand, textDecoration: 'none', fontSize: '.9rem' };
const message: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: '.7rem .9rem',
  marginBottom: '.7rem',
};
const fromUs: React.CSSProperties = { background: C.surface };
const messageHead: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '.6rem',
  marginBottom: '.35rem',
  fontSize: '.9rem',
  flexWrap: 'wrap',
};
